import { Component, Input, OnInit } from '@angular/core';
import { WorkflowApplicationNote } from 'src/app/models/workflow-application-note';
import { WorkflowContextService, WorkflowService } from 'src/app/services';

@Component({
  selector: 'app-workflow-application-notes',
  templateUrl: './workflow-application-notes.component.html',
  styleUrls: ['./workflow-application-notes.component.scss'],
  standalone: false
})
export class WorkflowApplicationNotesComponent implements OnInit {
  @Input() applicationId: string;

  notes: WorkflowApplicationNote[] = [];
  applicationNumber: string = '';
  newNote: string = '';
  editingNoteId: string | null = null;
  editingNoteText: string = '';
  isLoading: boolean = false;
  isSaving: boolean = false;
  currentUserName: string = '';

  constructor(
    private workflowService: WorkflowService,
    private _context: WorkflowContextService
  ) {}

  ngOnInit(): void {
    if (this._context.user) {
      this.currentUserName = this._context.user.userName;
    }
    if (this.applicationId) {
      this.loadNotes();
    }
  }

  loadNotes(): void {
    this.isLoading = true;
    this.workflowService.getWorkflowApplicationNotes(this.applicationId).subscribe({
      next: (response: any) => {
        this.notes = response.notes?.map((n: any) => new WorkflowApplicationNote(n)) || [];
        this.applicationNumber = response.applicationNumber || '';
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading notes:', error);
        this.isLoading = false;
      }
    });
  }

  addNote(): void {
    if (!this.newNote || !this.newNote.trim()) {
      return;
    }

    this.isSaving = true;
    const note = new WorkflowApplicationNote({
      id: '00000000-0000-0000-0000-000000000000',
      workflowApplicationId: this.applicationId,
      note: this.newNote.trim()
    });

    this.workflowService.saveWorkflowApplicationNote(note).subscribe({
      next: (savedNote) => {
        this.newNote = '';
        this.loadNotes();
        this.isSaving = false;
      },
      error: (error) => {
        console.error('Error saving note:', error);
        this.isSaving = false;
      }
    });
  }

  startEdit(note: WorkflowApplicationNote): void {
    this.editingNoteId = note.id;
    this.editingNoteText = note.note;
  }

  cancelEdit(): void {
    this.editingNoteId = null;
    this.editingNoteText = '';
  }

  saveEdit(note: WorkflowApplicationNote): void {
    if (!this.editingNoteText || !this.editingNoteText.trim()) {
      return;
    }

    this.isSaving = true;
    const updatedNote = new WorkflowApplicationNote({
      ...note,
      note: this.editingNoteText.trim()
    });

    this.workflowService.saveWorkflowApplicationNote(updatedNote).subscribe({
      next: () => {
        this.editingNoteId = null;
        this.editingNoteText = '';
        this.loadNotes();
        this.isSaving = false;
      },
      error: (error) => {
        console.error('Error updating note:', error);
        this.isSaving = false;
      }
    });
  }

  deleteNote(noteId: string): void {
    if (!confirm('Are you sure you want to delete this note?')) {
      return;
    }

    this.workflowService.deleteWorkflowApplicationNote(noteId).subscribe({
      next: () => {
        this.loadNotes();
      },
      error: (error) => {
        console.error('Error deleting note:', error);
      }
    });
  }

  markAsRead(note: WorkflowApplicationNote): void {
    this.workflowService.markWorkflowApplicationNoteAsRead(note.id).subscribe({
      next: (success) => {
        if (success) {
          this.loadNotes();
        }
      },
      error: (error) => {
        console.error('Error marking note as read:', error);
      }
    });
  }

  canMarkAsRead(note: WorkflowApplicationNote): boolean {
    return !note.isReadByCurrentUser && note.createdBy !== this.currentUserName;
  }
}
